/*
 * FP Rewards — the farm stand on /pages/rewards, playing a demo.
 *
 * The hero of the rewards page is the game's own 1600×900 scene, and FP throws
 * the three throws of one honest play at it, over and over: 3 FP from the
 * upper-left shelf, 2 FP from the middle with the bag below it chaining for
 * +1, then the jackpot with a perfect release for 6. The values are the
 * programme's real ones, so what a shopper watches is what a play pays.
 *
 * Ported from the design's own logic (tools/rewards-v2/design/rewards-v2.dc.html,
 * `class Component extends DCLogic`). The bag geometry and the plan are NOT
 * retyped here: the section carries them in `data-fp-stage`, read off the
 * design by tools/build-rewards-v2.mjs, so a re-laid-out stand travels.
 *
 * The same demo is visible to guests and members. Current values appear only
 * after the public program read succeeds. Without rates it remains an unpriced demo.
 *
 * NOTHING HERE TOUCHES A BALANCE. This file paints; fp-rewards.js is the one
 * that talks to the proxy. The only thing shared is `cents_per_point`, which
 * fp-rewards.js publishes on window.fpRewards once `summary` has answered, so
 * the result chip prices the demo at the live rate rather than a typed one.
 *
 * Loaded from the section (one section per page, so one execution). The load
 * guard below is the de-duplication, in the artifact that can observe its own
 * prior run — never a template-side flag.
 */
(function () {
  'use strict';
  if (window.fpRewardsStage) return;

  var STAND_TOP = 336;      // the stand group's top inside the 1600×900 canvas (design)
  var HAND_Y = 745.70;         // where the stick leaves FP's paw
  var STAND_LEFT = { wide: 998, compact: 800 };
  var FINLEY_LEFT = { wide: 190, compact: 560 };
  var FOCAL = { wide: 800, compact: 950 };
  var COMPACT_BELOW = 700;
  var IDLE = 'fprwIdle 4s step-end infinite';
  var BARK = 'fprwBark .42s steps(3) 1 both';
  var STILL_FRAME = '0px -612px';

  /* ── pure decisions, published for the tests ───────────────────────────── */

  /** Cover-scale the canvas into a W×H frame; in compact frames slide the
   *  focal point right so FP and the stand share the viewport. */
  function layoutFor(W, H) {
    var compact = W < COMPACT_BELOW;
    var s = Math.max(W / 1600, H / 900);
    var focal = compact ? FOCAL.compact : FOCAL.wide;
    var offX = Math.min(0, Math.max(W - 1600 * s, W / 2 - focal * s));
    var offY = (H - 900 * s) / 2;
    return {
      compact: compact, scale: s, offX: offX, offY: offY,
      standLeft: compact ? STAND_LEFT.compact : STAND_LEFT.wide,
      finleyLeft: compact ? FINLEY_LEFT.compact : FINLEY_LEFT.wide,
    };
  }

  /** Where a throw lands, in canvas px, and where it leaves the paw. */
  function throwPath(bag, standLeft, finleyLeft) {
    var bx = standLeft + bag.x + 74, by = STAND_TOP + bag.y + 84;
    var p0 = { x: finleyLeft + 31.68, y: HAND_Y };
    var p2 = { x: bx - 14, y: by };
    var p1 = { x: (p0.x + p2.x) / 2, y: Math.min(p0.y, p2.y) - 300 };
    return { p0: p0, p1: p1, p2: p2, hit: { x: bx, y: by }, callout: { x: bx, y: STAND_TOP + bag.y - 6 } };
  }

  /** A point on the quadratic curve, t in [0, 1]. */
  function curve(p0, p1, p2, t) {
    var u = 1 - t;
    return {
      x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
      y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
    };
  }

  /** What the callout says: the value for a member, a question for a guest. */
  function labelFor(step, signedIn) {
    if (signedIn) return step.label;
    return step.gold ? 'JACKPOT!' : 'DEMO';
  }

  /** The HUD figure and the result chip. */
  function scoreTextFor(score, signedIn) { return signedIn ? String(score) : 'DEMO'; }
  function resultTextFor(score, centsPerPoint, signedIn) {
    if (!signedIn || !Number.isFinite(centsPerPoint) || centsPerPoint <= 0) return 'Demo only';
    var cpp = centsPerPoint;
    return score + ' FP = $' + (score * cpp / 100).toFixed(2);
  }

  /** How long a bag takes to fall `dy` px. */
  function fallDuration(dy) { return Math.round(420 + dy * 1.2); }

  window.fpRewardsStage = {
    layoutFor: layoutFor, throwPath: throwPath, curve: curve, labelFor: labelFor,
    scoreTextFor: scoreTextFor, resultTextFor: resultTextFor, fallDuration: fallDuration,
  };

  /* ── the stage ─────────────────────────────────────────────────────────── */
  var root = document.querySelector('[data-fp-stage]');
  if (!root) return;

  var cfg;
  try { cfg = JSON.parse(root.getAttribute('data-fp-stage') || '{}'); } catch (e) { cfg = {}; }
  var BAGS = Array.isArray(cfg.bags) ? cfg.bags : [];
  var PLAN = Array.isArray(cfg.plan) ? cfg.plan : [];
  function programReady() { return root.getAttribute('data-program-ready') === '1'; }
  var wantDemo = root.getAttribute('data-demo') !== '0';
  var reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function R(name) { return root.querySelector('[data-r="' + name + '"]'); }
  var wrap = R('wrap'), canvas = R('canvas'), stand = R('stand'), finley = R('finley');
  var dog = R('dog'), held = R('held'), proj = R('proj'), fx = R('fx');
  var scoreEl = R('score'), resultEl = R('result'), resultTextEl = R('resultText');
  var pips = root.querySelectorAll('[data-r="pip"]');
  // The section hides the canvas until it is laid out (visibility, not display, so
  // the box keeps its size) — data-laid-out is the release. A scene missing a part
  // is shown as it is rather than kept hidden forever.
  if (!wrap || !canvas || !stand || !dog) { if (canvas) canvas.setAttribute('data-laid-out', '1'); return; }

  var geo = layoutFor(wrap.clientWidth, wrap.clientHeight);
  var visible = true, running = false, seq = 0;
  var timers = [];
  var fallen = {};
  var score = 0, left = 3;

  function centsPerPoint() {
    return Number(root.getAttribute('data-cents-per-point'));
  }

  function layout() {
    var W = wrap.clientWidth, H = wrap.clientHeight;
    if (!W || !H) return;
    geo = layoutFor(W, H);
    canvas.style.transform = 'translate(' + geo.offX + 'px,' + geo.offY + 'px) scale(' + geo.scale + ')';
    stand.style.left = geo.standLeft + 'px';
    if (finley) finley.style.left = geo.finleyLeft + 'px';
    if (held) held.style.left = (geo.finleyLeft + 17.68) + 'px';
    canvas.setAttribute('data-laid-out', '1');
  }

  /* timers: every one is tracked so stop() can clear the lot, and every
     callback checks the run it belongs to so a stopped run cannot paint */
  function after(ms, fn) {
    var mine = seq;
    var t = setTimeout(function () {
      timers.splice(timers.indexOf(t), 1);
      if (running && mine === seq) fn();
    }, ms);
    timers.push(t);
  }
  function stop() {
    running = false; seq++;
    timers.forEach(clearTimeout); timers = [];
    if (proj) proj.style.display = 'none';
  }

  /* ── HUD ──────────────────────────────────────────────────────────────── */
  function paintScore() {
    if (!scoreEl) return;
    scoreEl.textContent = scoreTextFor(score, programReady());
    // restart the bump: the design re-keyed the element, this re-arms the animation
    scoreEl.style.animation = 'none';
    void scoreEl.offsetWidth;
    scoreEl.style.animation = '';
  }
  function paintPips() {
    for (var i = 0; i < pips.length; i++) pips[i].classList.toggle('is-on', i < left);
  }
  function showResult(on) {
    if (!resultEl) return;
    if (on && resultTextEl) resultTextEl.textContent = resultTextFor(score, centsPerPoint(), programReady());
    resultEl.hidden = !on;
  }

  /* ── the effects ──────────────────────────────────────────────────────── */
  function effect(el, ttl) {
    if (!fx) return;
    fx.appendChild(el);
    var t = setTimeout(function () {
      timers.splice(timers.indexOf(t), 1);
      if (el.parentNode) el.parentNode.removeChild(el);
    }, ttl);
    timers.push(t);
  }
  function callout(x, y, text, gold) {
    var el = document.createElement('div');
    el.textContent = text;
    el.style.cssText = 'position:absolute;left:' + x + 'px;top:' + y + 'px;transform:translate(-50%,0);white-space:nowrap;padding:12px 24px;border-radius:999px;font:900 34px/1 var(--fp-font-body,Montserrat,sans-serif);letter-spacing:.04em;animation:fprwCallout 1.5s ease-out forwards;'
      + (gold ? 'background:linear-gradient(180deg,#F8CE63,#E0A32F);color:#5A2612;border:3px solid #FFF0BE;box-shadow:0 14px 34px rgba(40,10,6,.5),0 0 54px rgba(246,196,83,.55)'
              : 'background:#FFF9EF;color:#D2232A;border:3px solid #F0DBBB;box-shadow:0 14px 30px rgba(40,10,6,.45)');
    effect(el, 1600);
  }
  function burst(x, y, gold) {
    var ring = document.createElement('div');
    ring.style.cssText = 'position:absolute;left:' + (x - 40) + 'px;top:' + (y - 40) + 'px;width:80px;height:80px;border-radius:50%;border:3px dashed '
      + (gold ? '#F8CE63' : 'rgba(255,243,214,.9)') + ';animation:fprwRingOut .6s ease-out forwards';
    effect(ring, 700);
    for (var i = 0; i < 7; i++) {
      var c = document.createElement('div');
      var w = 5 + Math.random() * 5, h = 14 + Math.random() * 18;
      c.style.cssText = 'position:absolute;left:' + x + 'px;top:' + y + 'px;width:' + w + 'px;height:' + h + 'px;margin:' + (-h / 2) + 'px 0 0 ' + (-w / 2) + 'px;border-radius:' + w + 'px;background:linear-gradient(180deg,#C79A55,#8A5A2E)';
      effect(c, 1100);
      var a = -Math.PI * (0.15 + Math.random() * 0.7), d = 90 + Math.random() * 150;
      if (c.animate) {
        c.animate([
          { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
          { transform: 'translate(' + (Math.cos(a) * d) + 'px,' + (Math.sin(a) * d + 160) + 'px) rotate(' + ((Math.random() - 0.5) * 540) + 'deg)', opacity: 0 },
        ], { duration: 950, easing: 'cubic-bezier(.2,.6,.4,1)', fill: 'forwards' });
      }
    }
  }

  /* ── the bags ─────────────────────────────────────────────────────────── */
  function bags() { return Array.prototype.slice.call(stand.querySelectorAll('[data-bag]')); }
  function cancelAnimations(el) {
    if (el.getAnimations) el.getAnimations().forEach(function (a) { a.cancel(); });
  }
  function knock(el, idx, dy, dx, rot) {
    fallen[idx] = true;
    cancelAnimations(el);
    var dur = fallDuration(dy);
    if (!el.animate) { el.style.opacity = '0'; return; }
    el.animate([
      { transform: 'none' },
      { transform: 'translate(8px,-18px) rotate(-9deg)', offset: 0.14 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) rotate(' + rot + 'deg)', offset: 0.86 },
      { transform: 'translate(' + (dx + 4) + 'px,' + dy + 'px) rotate(' + (rot + 3) + 'deg)' },
    ], { duration: dur, easing: 'cubic-bezier(.5,0,.9,.6)', fill: 'forwards' });
    after(dur, function () {
      el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 340, delay: 220, fill: 'forwards' });
    });
  }
  function resetBags() {
    bags().forEach(function (el, i) {
      if (!fallen[i]) return;
      cancelAnimations(el);
      el.style.opacity = '';
      if (el.animate) {
        el.animate([
          { transform: 'scale(.4)', opacity: 0 },
          { transform: 'scale(1.08)', opacity: 1, offset: 0.7 },
          { transform: 'none', opacity: 1 },
        ], { duration: 420, delay: i * 70, easing: 'ease-out', fill: 'backwards' });
      }
    });
    fallen = {};
  }

  /* ── one throw ────────────────────────────────────────────────────────── */
  function fly(path, dur, done) {
    if (!proj) { done(); return; }
    var mine = seq;
    proj.style.display = 'block';
    var t0 = performance.now();
    function tick(now) {
      if (!running || mine !== seq) { proj.style.display = 'none'; return; }
      var t = Math.min(1, (now - t0) / dur);
      var p = curve(path.p0, path.p1, path.p2, t);
      proj.style.transform = 'translate(' + p.x + 'px,' + p.y + 'px) rotate(' + (-24 + t * 560) + 'deg)';
      if (t < 1) requestAnimationFrame(tick);
      else { proj.style.display = 'none'; done(); }
    }
    requestAnimationFrame(tick);
  }

  function throwAt(step, done) {
    var target = bags()[step.bag], g = BAGS[step.bag];
    if (!target || !g) { done(); return; }
    dog.style.animation = BARK;
    after(460, function () { dog.style.animation = IDLE; });
    after(150, function () {
      if (held) held.style.opacity = '0';
      var path = throwPath(g, geo.standLeft, geo.finleyLeft);
      fly(path, 700, function () {
        knock(target, step.bag, g.fall, step.dx, step.rot);
        burst(path.hit.x, path.hit.y, !!step.gold);
        callout(path.callout.x, path.callout.y, labelFor(step, programReady()), !!step.gold);
        score += step.gain; left = Math.max(0, left - 1);
        paintScore(); paintPips();
        if (step.chain != null && BAGS[step.chain]) {
          after(300, function () {
            var cb = bags()[step.chain], cg = BAGS[step.chain];
            if (!cb) return;
            knock(cb, step.chain, cg.fall, 34, 44);
            callout(geo.standLeft + cg.x + 74, STAND_TOP + cg.y + 30, programReady() ? '+1 CHAIN' : 'CHAIN!', false);
            score += 1; paintScore();
          });
        }
        after(520, function () { if (held) held.style.opacity = '1'; });
        after(fallDuration(g.fall) + 300, done);
      });
    });
  }

  /* ── the loop: a play, a pause, again ─────────────────────────────────── */
  function waitVisible(done) {
    if (visible && !document.hidden) { done(); return; }
    after(600, function () { waitVisible(done); });
  }
  function play(i, done) {
    if (i >= PLAN.length) { done(); return; }
    throwAt(PLAN[i], function () { after(PLAN[i].pause || 1000, function () { play(i + 1, done); }); });
  }
  function loop() {
    if (!running) return;
    resetBags();
    score = 0; left = 3;
    paintScore(); paintPips(); showResult(false);
    waitVisible(function () {
      after(1500, function () {
        play(0, function () {
          showResult(true);
          after(2800, function () {
            showResult(false);
            after(400, loop);
          });
        });
      });
    });
  }
  function start() {
    if (running) return;
    running = true; seq++;
    loop();
  }

  /* ── boot ─────────────────────────────────────────────────────────────── */
  layout();
  if (typeof ResizeObserver === 'function') new ResizeObserver(layout).observe(wrap);
  else window.addEventListener('resize', layout);
  if (typeof IntersectionObserver === 'function') {
    new IntersectionObserver(function (entries) {
      visible = !!(entries[0] && entries[0].isIntersecting);
    }, { threshold: 0.12 }).observe(wrap);
  }

  if (reduced) {
    // Park, do not delete: `animation:none` alone leaves the sprite on the
    // first cell of the sheet. Pin the still idle frame instead.
    dog.style.animation = 'none';
    dog.style.backgroundPosition = STILL_FRAME;
    paintScore(); paintPips(); showResult(false);
    return;
  }
  paintScore(); paintPips(); showResult(false);
  if (wantDemo && PLAN.length) start();

  window.fpRewardsStage.stop = stop;
  window.fpRewardsStage.start = start;
})();
